import { Controller } from "@hotwired/stimulus"

// Gap between the trigger and the bubble, and the height of the arrow that sits in it.
const ARROW_HEIGHT = 6
const GAP = 6
// Keep the bubble this far from the screen edge before shifting it back in.
const VIEWPORT_MARGIN = 8
// A [data-tooltip-boundary] ancestor, when present, is the box the bubble may not leave.
const BOUNDARY_SELECTOR = "[data-tooltip-boundary]"
// Keep the arrow this far from the bubble's rounded corners.
const ARROW_INSET = 10
// Only one tooltip is open at a time; opening one broadcasts this so the rest close.
const OPEN_EVENT = "cs-tooltip:open"

let idCounter = 0

export default class TooltipController extends Controller {
  static targets = ["trigger", "bubble", "arrow"]
  static values = { position: { type: String, default: "top" } }

  connect() {
    this.isOpen = false

    // role="tooltip" only helps if the trigger points at it.
    if (!this.bubbleTarget.id) this.bubbleTarget.id = `cs-tooltip-${++idCounter}`
    this.triggerTarget.setAttribute("aria-describedby", this.bubbleTarget.id)

    this._onOutsidePointer = (event) => {
      if (!this.element.contains(event.target)) this.hide()
    }
    this._onKeydown = (event) => {
      if (event.key === "Escape") this.hide()
    }
    this._onOtherOpened = (event) => {
      if (event.detail?.source !== this.element) this.hide()
    }
    this._onReflow = () => {
      if (!this.isOpen) return
      // The bubble is anchored to the trigger, so it travels with it — only the
      // flip and the horizontal clamp need recomputing.
      if (this._reflowFrame) return
      this._reflowFrame = requestAnimationFrame(() => {
        this._reflowFrame = null
        if (this.isOpen) this._place()
      })
    }

    document.addEventListener(OPEN_EVENT, this._onOtherOpened)
  }

  disconnect() {
    this._teardownDismissListeners()
    document.removeEventListener(OPEN_EVENT, this._onOtherOpened)
    if (this._reflowFrame) {
      cancelAnimationFrame(this._reflowFrame)
      this._reflowFrame = null
    }
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    this.isOpen ? this.hide() : this.show()
  }

  // Touch browsers synthesise mouseenter on tap, which would open the bubble a
  // beat before the click closes it again. Hover is for real pointers only.
  showOnHover() {
    if (window.matchMedia("(hover: hover)").matches) this.show()
  }

  hideOnHover() {
    if (window.matchMedia("(hover: hover)").matches) this.hide()
  }

  // A mouse click focuses the trigger too, so react to keyboard focus only —
  // otherwise focus opens the bubble and the click that caused it closes it.
  showOnFocus() {
    if (this.triggerTarget.matches(":focus-visible")) this.show()
  }

  hideOnFocus() {
    if (!this.triggerTarget.matches(":hover")) this.hide()
  }

  show() {
    if (this.isOpen) return
    this.isOpen = true

    document.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: { source: this.element } }))

    this.bubbleTarget.dataset.open = "true"
    this.triggerTarget.setAttribute("aria-expanded", "true")
    this._place()

    document.addEventListener("pointerdown", this._onOutsidePointer, true)
    document.addEventListener("keydown", this._onKeydown)
    window.addEventListener("resize", this._onReflow)
    window.addEventListener("scroll", this._onReflow, true)
  }

  hide() {
    if (!this.isOpen) return
    this.isOpen = false

    this.bubbleTarget.dataset.open = "false"
    this.triggerTarget.setAttribute("aria-expanded", "false")
    this._teardownDismissListeners()
  }

  _teardownDismissListeners() {
    document.removeEventListener("pointerdown", this._onOutsidePointer, true)
    document.removeEventListener("keydown", this._onKeydown)
    window.removeEventListener("resize", this._onReflow)
    window.removeEventListener("scroll", this._onReflow, true)
  }

  // Picks the side that actually fits, then slides the bubble back on screen and
  // walks the arrow the other way so it stays over the trigger.
  _place() {
    const bubble = this.bubbleTarget

    bubble.style.setProperty("--cs-tooltip-shift", "0px")
    bubble.dataset.side = this._fittingSide()

    const rect = bubble.getBoundingClientRect()
    const bounds = this._bounds()
    let shift = 0
    if (rect.width >= bounds.right - bounds.left || rect.left < bounds.left) {
      // Too wide to fit, or hanging off the left: pin the left edge. The left is the side
      // that must never be clipped, so it wins when both edges can't be satisfied.
      shift = bounds.left - rect.left
    } else if (rect.right > bounds.right) {
      shift = bounds.right - rect.right
    }

    // The arrow can only travel as far as the bubble's straight edge allows.
    const arrowLimit = Math.max(0, rect.width / 2 - ARROW_INSET)
    const arrowShift = Math.max(-arrowLimit, Math.min(arrowLimit, -shift))

    bubble.style.setProperty("--cs-tooltip-shift", `${shift}px`)
    this.arrowTarget.style.setProperty("--cs-tooltip-arrow-shift", `${arrowShift}px`)
  }

  // The horizontal box the bubble has to stay inside. Defaults to the viewport; a
  // [data-tooltip-boundary] ancestor narrows it to that element — the fee card, say, so the
  // bubble never hangs past the card's edge. The viewport still caps it either way.
  _bounds() {
    const viewportLeft = VIEWPORT_MARGIN
    const viewportRight = window.innerWidth - VIEWPORT_MARGIN

    const boundary = this.element.closest(BOUNDARY_SELECTOR)
    if (!boundary) return { left: viewportLeft, right: viewportRight }

    const box = boundary.getBoundingClientRect()
    return {
      left: Math.max(box.left, viewportLeft),
      right: Math.min(box.right, viewportRight),
    }
  }

  _fittingSide() {
    const preferred = this.positionValue === "bottom" ? "bottom" : "top"

    const trigger = this.triggerTarget.getBoundingClientRect()
    const needed = this.bubbleTarget.getBoundingClientRect().height + ARROW_HEIGHT + GAP
    const roomAbove = trigger.top
    const roomBelow = window.innerHeight - trigger.bottom

    if (preferred === "top" && roomAbove < needed && roomBelow >= needed) return "bottom"
    if (preferred === "bottom" && roomBelow < needed && roomAbove >= needed) return "top"
    return preferred
  }
}
